import { useState } from "react";
import type { ChildProfile } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { Paywall } from "../components/Paywall";

/**
 * All that remains of the grown-ups view on the tablet: the sky name, and a way
 * to hand the device to a sibling. Everything else — rewards, prices, the
 * weekly digest, the safety flags — lives on the parent's own page now.
 *
 * The gate is real for the first time. The password is set by the adult from a
 * link we emailed them, so a child can no longer watch it being chosen, and
 * unlocking this screen returns no parent token — there is nothing here that
 * could open the portal on a device a child is holding.
 */
export function DeviceScreen({
  profile,
  onExit,
  onSignOut,
}: {
  profile: ChildProfile;
  onExit: () => void;
  onSignOut: () => void;
}) {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    if (!password || busy) return;
    setError("");
    setBusy(true);
    try {
      await api.deviceUnlock(password);
      setUnlocked(true);
      setPassword("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't check that.");
    } finally {
      setBusy(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="rise safe-top mx-auto max-w-md px-6 pt-10">
        <p className="disp text-[28px] text-cream">Grown-ups</p>
        <p className="mb-6 mt-1 leading-relaxed text-cream/60">
          Enter the password you made from the email we sent you.
        </p>

        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void unlock()}
          placeholder="Your password"
          className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[18px] text-cream outline-none placeholder:text-cream/35"
        />

        {error && <p className="mt-3 text-[15px] text-amber">{error}</p>}

        <button
          onClick={() => void unlock()}
          disabled={!password || busy}
          className="kidbtn disp mt-4 w-full rounded-3xl bg-gold py-4 text-[19px] text-night disabled:bg-cream/15 disabled:text-cream/40"
        >
          {busy ? "Checking…" : "Unlock"}
        </button>

        {/* The full parent view is somewhere else on purpose. Saying so here
            is the difference between a parent thinking it's missing and a
            parent knowing where to look. */}
        <p className="mt-6 rounded-2xl bg-panel p-4 text-[15px] leading-relaxed text-cream/60">
          Rewards, prices and this week's summary are on your own page — open{" "}
          <b className="text-cream">howcomet.com/parents</b> on your phone or computer.
        </p>

        <button onClick={onExit} className="kidbtn mt-4 w-full py-2 text-[15px] text-cream/50">
          Back to kid view
        </button>
      </div>
    );
  }

  return (
    <div className="rise safe-top mx-auto max-w-md px-6 pt-10">
      <div className="mb-6 flex items-center justify-between">
        <p className="disp text-[28px] text-cream">This device</p>
        <button
          onClick={onExit}
          className="kidbtn disp rounded-full bg-gold px-4 py-2 text-[14px] text-night"
        >
          Back to kid view
        </button>
      </div>

      {/*
        The purchase lives behind this password, which is the parental gate the
        Kids Category asks for — and a real one, since the adult set it from an
        emailed link on their own device.
      */}
      <Paywall onChanged={onExit} />

      <div className="mt-5 rounded-3xl bg-panel p-5">
        <p className="text-cream/75">
          Signed in as <b className="text-cream">{profile.name}</b> · {profile.gradeLabel}
        </p>

        {/* Worth having: a child who was shown this at five is a different
            child at seven, and a second one arriving on the same tablet has
            never seen it at all. */}
        <button
          onClick={() => {
            void api.guideReset();
            onExit();
          }}
          className="kidbtn mt-4 w-full rounded-2xl bg-cream/10 px-5 py-3 text-[16px] font-bold text-cream"
        >
          Show them how it works again
        </button>

        <p className="mt-4 text-[14px] uppercase tracking-wider text-cream/45">Sky name</p>
        <p className="disp select-all text-[22px] tracking-wide text-gold">{profile.handle}</p>
        <p className="mt-1 text-[14px] leading-relaxed text-cream/55">
          This plus their four-digit number is how they sign in on another tablet. Write it down —
          there's no email on a child's account, so there's nothing to reset it against.
        </p>

        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="kidbtn mt-4 rounded-full bg-cream/12 px-5 py-2 font-bold text-cream"
          >
            Sign out
          </button>
        ) : (
          <div className="mt-4 rounded-2xl border-2 border-amber p-4">
            <p className="font-bold text-amber">Sign {profile.name} out of this tablet?</p>
            <p className="mt-2 text-[15px] leading-relaxed text-cream/75">
              Their sky and stardust are safe — nothing is deleted. To get back in they'll need{" "}
              <b className="text-cream">{profile.handle}</b> and their number.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={onSignOut}
                className="kidbtn rounded-full bg-amber px-5 py-2 font-extrabold text-night"
              >
                Yes, sign out
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="kidbtn rounded-full bg-cream/12 px-5 py-2 font-bold text-cream"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
